import settingsService, {
  getIsUndockedEnabled,
  setIsUndockedEnabled,
  externalNotifyUndockedChanged
} from '~/services/room-settings'
import { ACTION_TYPES, BRIDGE_EVENTS, LOBBY_TYPE, MODERATION_TYPE } from '~/core/constants'
import { logInfo } from '~/core/logger'
import analytics from '~/services/analytics'
import api from '~/services/api'
import { translate } from '~/core/translate'
import bridge from '~/services/bridge'

import { showErrorNotification } from '~/core/ui'
import { getFullNameFromUrl, getNameFromUrl, getLobbySettings } from '~/core/utils'
import {
  selectMeetingPreviewEnabled,
  isUndockedEnabled,
  getModerationSettings
} from '~/selectors/room-settings'
import { getViewedSessionId } from '~/selectors/ui'
import { getShouldLaunchUndocked } from '~/selectors/app'
import { setPersonalRoom } from './room'

const { ROOM_SETTINGS } = ACTION_TYPES

const setPersonalRoomPassword = password => ({
  type: ROOM_SETTINGS.SET_PERSONAL_ROOM_PASSSWORD,
  payload: password
})

const setMeetingPreview = payload => ({
  type: ROOM_SETTINGS.SET_MEETING_PREVIEW_ENABLED,
  payload
})

const setUndocked = payload => ({
  type: ROOM_SETTINGS.SET_UNDOCKED,
  payload
})

export const notifyUndockedChanged = value => {
  externalNotifyUndockedChanged(value)
  bridge.emit(BRIDGE_EVENTS.UPDATE_UNDOCKED, value)
}

export const getUndockedSetting = () => async dispatch => {
  const value = await getIsUndockedEnabled()
  dispatch(setUndocked(value))
  notifyUndockedChanged(value)
}

export const toggleUndocked = () => async (dispatch, getState) => {
  const next = !isUndockedEnabled(getState())

  setIsUndockedEnabled(next)
  dispatch(setUndocked(next))
  notifyUndockedChanged(next)
}

export const getPreviewSettings = () => async dispatch => {
  const value = await settingsService.getIsAlwaysOnTopEnabled()
  dispatch(setMeetingPreview(value))
}

export const toggleMeetingPreview = () => (dispatch, getState) => {
  const state = getState()
  const current = selectMeetingPreviewEnabled(state)
  const next = !current
  const isUndocked = getShouldLaunchUndocked(state)

  settingsService.setIsAlwaysOnTopEnabled(next, isUndocked)
  dispatch(setMeetingPreview(next))
}

export const updatePersonalRoomName = roomName => async dispatch => {
  logInfo({ __filename, roomName }, 'Updating personal room name')
  analytics.trackInitEvent(analytics.EVENT_NAMES.EDIT_PERSONAL_MEETING_NAME)
  const resp = await settingsService.updatePersonalRoomName(roomName)

  if (api.isErrorResponse(resp)) {
    analytics.trackFailedEvent(analytics.EVENT_NAMES.EDIT_PERSONAL_MEETING_NAME, {
      reason: resp.message
    })
    showErrorNotification(translate('error_updating_personal_room'))

    throw Error('error updating personal room name')
  }

  const fullName = getFullNameFromUrl(resp.url)
  const name = getNameFromUrl(resp.url)
  const room = { name, fullName, url: resp.url }

  dispatch(setPersonalRoom(room))

  return room
}

export const updatePersonalRoomPassword = password => async dispatch => {
  logInfo({ __filename }, 'Updating personal room password')
  analytics.trackInitEvent(analytics.EVENT_NAMES.EDIT_PERSONAL_MEETING_PASSWORD)
  const resp = await settingsService.updatePersonalRoomPassword(password)

  if (api.isErrorResponse(resp)) {
    analytics.trackFailedEvent(analytics.EVENT_NAMES.EDIT_PERSONAL_MEETING_PASSWORD, {
      reason: resp.message
    })
    showErrorNotification(translate('error_updating_personal_room'))

    throw Error('error updating personal room passcode')
  }

  dispatch(setPersonalRoomPassword(resp.passcode))
  return resp.passcode
}

export const deletePersonalRoomPassword = () => async dispatch => {
  logInfo({ __filename }, 'Removing personal room password')
  analytics.trackInitEvent(analytics.EVENT_NAMES.EDIT_PERSONAL_MEETING_PASSWORD, {
    delete: true
  })
  const resp = await settingsService.removePersonalRoomPassword()

  if (api.isErrorResponse(resp)) {
    analytics.trackFailedEvent(analytics.EVENT_NAMES.EDIT_PERSONAL_MEETING_PASSWORD, {
      reason: resp.message,
      delete: true
    })
    showErrorNotification(translate('error_updating_personal_room'))

    throw Error('error deleting personal room passcode')
  }

  dispatch(setPersonalRoomPassword(''))
  return ''
}

export const setUserIdentity = payload => ({
  type: ROOM_SETTINGS.SET_USER_IDENTITY,
  payload
})

const setAccessSettings = payload => ({
  type: ROOM_SETTINGS.SET_ACCESS_SETTINGS,
  payload
})

const setModerationSettings = payload => ({
  type: ROOM_SETTINGS.SET_MODERATION_SETTINGS,
  payload
})

const setRecordingSettings = payload => ({
  type: ROOM_SETTINGS.SET_RECORDING_SETTINGS,
  payload
})

const setSecuritySettings = payload => ({
  type: ROOM_SETTINGS.SET_SECURITY_SETTINGS,
  payload
})

const setIntendedPreset = payload => ({
  type: ROOM_SETTINGS.SET_INTENDED_PRESET,
  payload
})

const setOverridePermissions = payload => ({
  type: ROOM_SETTINGS.SET_OVERRIDE_PERMISSIONS,
  payload
})

export const applyPreset = ({ value }) => async dispatch => {
  dispatch(updateMeetingProperties({
    intendedPreset: value
  }))
}

export const getMeetingProperties = meetingFqn => async dispatch => {
  const response = await settingsService.fetchMeetingProperties(meetingFqn)

  if (api.isErrorResponse(response)) {
    showErrorNotification(translate('error_fetching_meeting_properties'))

    throw new Error(response?.status === 403 ? 'Unauthorized' : 'Failed to fetch meeting properties')
  }

  const {
    allowUnauthenticatedAccess,
    dialinEnabled,
    autoVideoRecording,
    autoAudioRecording,
    autoTranscriptions,
    moderationType,
    joinMuted,
    joinHidden,
    abilityToUnmute,
    lobbyEnabled,
    lobbyType,
    passcode,
    intendedPreset,
    activeParticipantUsers,
    goLiveEnabled,
    visitorPromotionType,
    overridePermissions
  } = response

  dispatch(setAccessSettings({
    allowUnauthenticatedAccess,
    dialinEnabled
  }))
  dispatch(setModerationSettings({
    moderationType,
    joinHidden,
    joinMuted,
    abilityToUnmute,
    visitorPromotionType,
    activeParticipantUsers
  }))
  dispatch(setRecordingSettings({
    autoVideoRecording,
    autoAudioRecording,
    autoTranscriptions
  }))
  dispatch(setSecuritySettings({
    goLiveEnabled,
    passcode,
    waitForApproval: (lobbyEnabled || !overridePermissions.lobbyEnabled) && lobbyType === LOBBY_TYPE.WAIT_FOR_APPROVAL,
    waitForModerator: (lobbyEnabled || !overridePermissions.lobbyEnabled) && (lobbyType === LOBBY_TYPE.WAIT_FOR_MODERATOR || lobbyType === LOBBY_TYPE.WAIT_FOR_APPROVAL)
  }))
  dispatch(setIntendedPreset({
    intendedPreset
  }))
  dispatch(setOverridePermissions(overridePermissions))

  return response
}

export const updateMeetingProperties = payload => async (dispatch, getState) => {
  const meetingFqn = getViewedSessionId(getState())
  logInfo({ __filename, meetingFqn }, 'Updating moderation settings')

  const response = await settingsService.updateMeetingProperties({
    ...payload,
    meetingFqn,
    ...getLobbySettings(payload)
  })
  if (api.isErrorResponse(response)) {
    showErrorNotification(translate('error_updating_meeting_properties'))

    return
  }

  dispatch(setAccessSettings(response))
  dispatch(setModerationSettings(response))
  dispatch(setRecordingSettings(response))

  const { goLiveEnabled, passcode = '', lobbyType, lobbyEnabled } = response
  dispatch(setSecuritySettings({
    goLiveEnabled,
    passcode,
    waitForApproval: lobbyEnabled && lobbyType === LOBBY_TYPE.WAIT_FOR_APPROVAL,
    waitForModerator: lobbyEnabled && lobbyType === LOBBY_TYPE.WAIT_FOR_MODERATOR
  }))
  dispatch(setIntendedPreset(response))
}

export const updateAccessSettings = payload => async (dispatch, getState) => {
  const meetingFqn = getViewedSessionId(getState())
  logInfo({ __filename, meetingFqn }, 'Updating access settings')

  const response = await settingsService.updateMeetingProperties({
    ...payload,
    meetingFqn
  })

  if (api.isErrorResponse(response)) {
    showErrorNotification(translate('error_updating_access_properties'))

    return
  }

  dispatch(setAccessSettings(response))
}

export const updateModerationSettings = payload => async (dispatch, getState) => {
  const meetingFqn = getViewedSessionId(getState())
  logInfo({ __filename, meetingFqn }, 'Updating moderation settings')

  const response = await settingsService.updateMeetingProperties({
    ...payload,
    meetingFqn
  })

  if (api.isErrorResponse(response)) {
    const { status, args = [] } = response || {}
    if (status === 400 && args?.[0]?.key === 'limit') {
      showErrorNotification(translate('active_participant_limit_reached', { limit: args?.[0]?.value }))
    } else {
      showErrorNotification(translate('error_updating_moderation_properties'))
    }

    return
  }

  dispatch(setModerationSettings(response))
}

export const updateActiveParticipantRole = ({ userId, role }) => async (dispatch, getState) => {
  const { activeParticipantUsers } = getModerationSettings(getState())

  dispatch(updateModerationSettings({
    moderationType: MODERATION_TYPE.SPECIFIC_MEMBERS,
    activeParticipantUsers: activeParticipantUsers.map(user => {
      if (user.userId === userId) {
        return {
          ...user,
          activeParticipantType: role
        }
      }
      return user
    })
  }))
}

export const updateRecordingSettings = payload => async (dispatch, getState) => {
  const meetingFqn = getViewedSessionId(getState())

  logInfo({ __filename, meetingFqn }, 'Updating recording settings')

  const response = await settingsService.updateMeetingProperties({
    meetingFqn,
    ...payload
  })
  if (api.isErrorResponse(response)) {
    showErrorNotification(translate('error_updating_recording_properties'))

    return
  }

  dispatch(setRecordingSettings(response))
}

export const updateSecuritySettings = payload => async (dispatch, getState) => {
  const meetingFqn = getViewedSessionId(getState())

  logInfo({ __filename, meetingFqn }, 'Updating security settings')

  const { waitForApproval, waitForModerator, ...rest } = payload
  const response = await settingsService.updateMeetingProperties({
    meetingFqn,
    ...rest,
    ...getLobbySettings(payload)
  })
  if (api.isErrorResponse(response)) {
    showErrorNotification(translate('error_updating_security_properties'))

    return
  }

  const { goLiveEnabled, passcode = '', lobbyType, lobbyEnabled } = response
  dispatch(setSecuritySettings({
    goLiveEnabled,
    passcode,
    waitForApproval: lobbyEnabled && lobbyType === LOBBY_TYPE.WAIT_FOR_APPROVAL,
    waitForModerator: lobbyEnabled && lobbyType === LOBBY_TYPE.WAIT_FOR_MODERATOR
  }))
}

export const getContacts = query => async () => {
  const response = await settingsService.fetchAppContacts(query)
  if (api.isErrorResponse(response)) {
    showErrorNotification(translate('error_retrieving_contacts'))

    return []
  }

  return response
}
